/**
 * O cartão de um número de WhatsApp na lista de canais (30/09/2026).
 *
 * Em cima, o número e as ações que valem para ele inteiro. Embaixo, duas
 * abas — Mensagens automáticas e Modelos de mensagem —, cada uma com o resumo
 * ao lado ("saudação no ar", "6 aprovados") e o seu próprio painel.
 *
 * ── Por que abas, e não os dois blocos um embaixo do outro ────────────────
 *
 * Os dois blocos cabiam no cartão enquanto ele quebrava linha. Quando a lista
 * de canais mudou de contêiner (#552), a regra que o fazia quebrar deixou de
 * valer, e tudo foi espremido numa linha só. Abas resolvem de vez: o cartão
 * fechado tem uma altura só, e aberto mostra um painel por vez, na largura
 * inteira.
 *
 * Clicar na aba aberta fecha o painel: o cartão volta a ser uma linha da
 * lista, que é o estado de quem só está conferindo o que está conectado.
 */

import { useId, useState, type ReactNode } from 'react';
import type { CanalDeWhatsApp } from '@cliqcrm/contracts';
import { Cartao, Selo } from '@/shared/ui';
import { useModelosDeMensagem } from '@/shared/api/consultas';
import { LogoDoCanal } from './canais';
import { MensagensAutomaticas, resumoDasMensagensAutomaticas } from './mensagens-automaticas';
import { ModelosDeMensagem, resumoDosModelos } from './modelos-de-mensagem';

type Aba = 'automaticas' | 'modelos';

export function CartaoDoNumero({
  canal,
  telefone,
  acoes,
}: {
  canal: CanalDeWhatsApp;
  /** O telefone já formatado para leitura. */
  telefone: string;
  /** Trocar conta, Atendentes, Desconectar: o que vale para o número inteiro. */
  acoes: ReactNode;
}): ReactNode {
  const [aberta, definirAberta] = useState<Aba | null>(null);
  const modelos = useModelosDeMensagem(canal.referencia_externa);
  const id = useId();

  const abas: { chave: Aba; nome: string; resumo: string }[] = [
    {
      chave: 'automaticas',
      nome: 'Mensagens automáticas',
      resumo: resumoDasMensagensAutomaticas(canal.mensagens_automaticas),
    },
    {
      chave: 'modelos',
      nome: 'Modelos de mensagem',
      resumo: modelos.data ? resumoDosModelos(modelos.data.itens) : '…',
    },
  ];

  return (
    <Cartao como="li" parte="cartao-numero">
      <div className="cartao-numero__cabeca">
        <LogoDoCanal tipo="whatsapp" />
        <span className="cartao-numero__id">
          <span className="cartao-numero__nome">{canal.nome}</span>
          <span className="cartao-numero__fone">{telefone}</span>
        </span>
        <Selo tom="ok">Conectado</Selo>
        <span className="cartao-numero__acoes">{acoes}</span>
      </div>

      {/*
        Botões que abrem e fecham um painel, e não `role="tab"`: aqui nenhuma
        aba precisa estar sempre escolhida, e o leitor de tela anuncia
        "expandido" e "recolhido", que é exatamente o que acontece.
      */}
      <div className="cartao-numero__abas">
        {abas.map((aba) => (
          <button
            key={aba.chave}
            type="button"
            className="cartao-numero__aba"
            aria-expanded={aberta === aba.chave}
            aria-controls={`${id}-${aba.chave}`}
            onClick={() => definirAberta((atual) => (atual === aba.chave ? null : aba.chave))}
          >
            {aba.nome}
            <span className="cartao-numero__resumo">{aba.resumo}</span>
          </button>
        ))}
      </div>

      {aberta && (
        <div
          id={`${id}-${aberta}`}
          className="cartao-numero__painel"
          role="region"
          aria-label={abas.find((a) => a.chave === aberta)!.nome}
        >
          {aberta === 'automaticas' ? (
            <MensagensAutomaticas canal={canal} />
          ) : (
            <ModelosDeMensagem canal={canal} />
          )}
        </div>
      )}
    </Cartao>
  );
}
